import { useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { GraphQLError } from '@bmwe/data';
import { useToast } from '../../context/toast';
import styles from './errorFallback.module.css';

export interface ErrorFallbackProps {
  error?: unknown;
  message?: string;
  onRetry?: () => void;
}

export function ErrorFallback({ error, message, onRetry }: ErrorFallbackProps) {
  const { showSuccess, showError } = useToast();
  const [copied, setCopied] = useState(false);

  const summary = useMemo(() => {
    if (message) return message;
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    return 'Etwas ist schiefgelaufen!';
  }, [error, message]);

  const details = useMemo(
    () => buildErrorReport(error, summary),
    [error, summary],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(details);
      setCopied(true);
      showSuccess('Fehlerdetails kopiert');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      showError('Kopieren fehlgeschlagen');
    }
  };

  return (
    <div className={styles['container']} role="alert" aria-live="assertive">
      <div className={styles['dialog']}>
        <div className={styles['header']}>
          <i
            className={`pi pi-exclamation-triangle ${styles['icon']}`}
            aria-hidden
          />
          <h2 className={styles['title']}>Etwas ist schiefgelaufen</h2>
        </div>

        <p className={styles['summary']}>{summary}</p>

        <div className={styles['detailsHeader']}>
          <p className={styles['detailsLabel']}>
            Fehlerdetails (zum Melden des Bugs)
          </p>
          <Button
            type="button"
            size="small"
            severity="secondary"
            text
            icon={copied ? 'pi pi-check' : 'pi pi-copy'}
            label={copied ? 'Kopiert' : 'Kopieren'}
            onClick={handleCopy}
            aria-label="Fehlerdetails kopieren"
          />
        </div>

        <div className={styles['textareaWrapper']}>
          <textarea
            className={styles['textarea']}
            value={details}
            readOnly
            spellCheck={false}
            onFocus={(e) => e.currentTarget.select()}
          />
        </div>

        <div className={styles['actions']}>
          {onRetry ? (
            <Button
              type="button"
              size="small"
              label="Erneut versuchen"
              icon="pi pi-refresh"
              onClick={onRetry}
            />
          ) : (
            <Button
              type="button"
              size="small"
              label="Seite neu laden"
              icon="pi pi-refresh"
              onClick={() => window.location.reload()}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function buildErrorReport(error: unknown, summary: string): string {
  const lines: string[] = [];
  const timestamp = new Date().toISOString();

  lines.push(`Zeitpunkt: ${timestamp}`);
  if (typeof window !== 'undefined') {
    lines.push(`URL: ${window.location.href}`);
    lines.push(`User-Agent: ${navigator.userAgent}`);
  }
  lines.push('');

  if (error instanceof GraphQLError) {
    lines.push(`Typ: GraphQL-Fehler (HTTP ${error.context.status})`);
    if (error.context.operationName) {
      lines.push(`Operation: ${error.context.operationName}`);
    }
    lines.push(`Endpoint: ${error.context.endpoint}`);
    lines.push('');
    lines.push('Meldung:');
    lines.push(error.message);
    lines.push('');
    lines.push('GraphQL-Errors:');
    lines.push(safeStringify(error.context.errors));
    lines.push('');
    lines.push('Variablen:');
    lines.push(safeStringify(error.context.variables));
    lines.push('');
    lines.push('Query:');
    lines.push(error.context.query.trim());
    return lines.join('\n');
  }

  if (error instanceof Error) {
    lines.push(`Typ: ${error.name}`);
    lines.push('');
    lines.push('Meldung:');
    lines.push(error.message);
    if (error.stack) {
      lines.push('');
      lines.push('Stacktrace:');
      lines.push(error.stack);
    }
    return lines.join('\n');
  }

  lines.push('Meldung:');
  lines.push(summary);
  if (error !== undefined && error !== null) {
    lines.push('');
    lines.push('Daten:');
    lines.push(safeStringify(error));
  }
  return lines.join('\n');
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}
